import { Card, DatePicker, Flex, Select, Skeleton, Typography } from "antd";
import { useMemo, useState } from "react";
import type { Options, SeriesOptionsType, YAxisOptions } from "highcharts";
import { Chart } from "@highcharts/react";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import type {
    DeliveryType,
    OrdersStatisticsMetric,
    OrdersStatisticsSeriesBucket,
    OrdersStatus,
    StatisticsGranularity,
} from "@src/types";
import { ORDERS_STATISTICS_DEFAULT_METRICS, useMerchants, useOrdersStatistics } from "@src/queries";

type AxisId = "primary" | "secondary";

interface MetricConfig {
    label: string;
    seriesType: SeriesOptionsType["type"];
    axis: AxisId;
    tooltipLabel?: string;
}

const METRIC_CONFIG: Record<OrdersStatisticsMetric, MetricConfig> = {
    count: {
        label: "Количество заказов",
        seriesType: "column",
        axis: "primary",
        tooltipLabel: "заказов",
    },
    total_price: {
        label: "Оборот",
        seriesType: "line",
        axis: "secondary",
        tooltipLabel: "KZT",
    },
};

const METRIC_ORDER: Record<OrdersStatisticsMetric, number> = {
    count: 0,
    total_price: 1,
};

const GRANULARITY_LABELS: Record<StatisticsGranularity, string> = {
    DAILY: "По дням",
    WEEKLY: "По неделям",
    HALF_MONTHLY: "По половинам месяца",
    MONTHLY: "По месяцам",
};

const GRANULARITY_OPTIONS = (
    Object.entries(GRANULARITY_LABELS) as Array<[StatisticsGranularity, string]>
).map(([value, label]) => ({ value, label }));

const DELIVERY_TYPE_LABELS: Record<DeliveryType, string> = {
    PICKUP: "Самовывоз",
    DELIVERY: "Моя Доставка",
    KASPI_DELIVERY: "Zammler доставка",
    KASPI_DELIVERY_POST: "Zammler Postomat",
    KASPI_DELIVERY_EXPRESS: "Kaspi Express",
};

const DELIVERY_TYPE_OPTIONS = (
    Object.entries(DELIVERY_TYPE_LABELS) as Array<[DeliveryType, string]>
).map(([value, label]) => ({ value, label }));

const ORDER_STATUS_LABELS: Record<OrdersStatus, string> = {
    APPROVED_BY_BANK: "Одобрено банком",
    ACCEPTED_BY_MERCHANT: "Принято мерчантом",
    COMPLETED: "Завершено",
    CANCELLED: "Отменено",
    CANCELLING: "Отмена в процессе",
    KASPI_DELIVERY_RETURN_REQUESTED: "Возврат Kaspi запрошен",
    RETURNED: "Возвращено",
};

const ORDER_STATUS_OPTIONS = (
    Object.entries(ORDER_STATUS_LABELS) as Array<[OrdersStatus, string]>
).map(([value, label]) => ({ value, label }));

type PreOrderFilterValue = "all" | "true" | "false";

const PRE_ORDER_SELECT_OPTIONS: { value: PreOrderFilterValue; label: string }[] = [
    { value: "all", label: "Все заказы" },
    { value: "true", label: "Только предзаказы" },
    { value: "false", label: "Только обычные" },
];

const { RangePicker } = DatePicker;

const DEFAULT_CHART_HEIGHT = 420;

function parseDecimal(value?: string): number {
    if (typeof value !== "string") return 0;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function buildSeriesData(
    metric: OrdersStatisticsMetric,
    buckets: OrdersStatisticsSeriesBucket[],
): number[] {
    return buckets.map((bucket) => parseDecimal(bucket[metric]));
}

function getMetricLabel(metric: OrdersStatisticsMetric): string {
    return METRIC_CONFIG[metric]?.label ?? metric;
}

const numberFormatter = new Intl.NumberFormat("ru-RU");

function formatAxisLabel(value: number, metric: OrdersStatisticsMetric): string {
    if (metric === "total_price") {
        return `${numberFormatter.format(value)} ₸`;
    }
    return numberFormatter.format(value);
}

export function OrdersStatisticsChart() {
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => {
        const end = dayjs().endOf("month");
        return [end.subtract(3, "month").startOf("month"), end];
    });
    const [granularity, setGranularity] = useState<StatisticsGranularity>("HALF_MONTHLY");
    const [selectedMetrics, setSelectedMetrics] = useState<OrdersStatisticsMetric[]>(() => [
        ...ORDERS_STATISTICS_DEFAULT_METRICS,
    ]);
    const [deliveryType, setDeliveryType] = useState<DeliveryType | null>(null);
    const [preOrderFilter, setPreOrderFilter] = useState<PreOrderFilterValue>("all");
    const [status, setStatus] = useState<OrdersStatus | null>("COMPLETED");
    const [merchantId, setMerchantId] = useState<string | null>(null);

    const merchantsQuery = useMerchants();
    const merchantOptions = merchantsQuery.data?.data ?? [];

    const [startDate, endDate] = useMemo(() => {
        const [start, end] = dateRange;
        return [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")];
    }, [dateRange]);

    const preOrderParam = useMemo<boolean | null>(() => {
        if (preOrderFilter === "all") return null;
        return preOrderFilter === "true";
    }, [preOrderFilter]);

    const canRequestData = selectedMetrics.length > 0 && Boolean(startDate && endDate);

    const { data, isPending, isError, error } = useOrdersStatistics({
        startDate,
        endDate,
        granularity,
        metrics: selectedMetrics,
        deliveryType,
        preOrder: preOrderParam,
        status,
        merchantId,
        enabled: canRequestData,
    });

    const responseMetrics = data?.meta.metrics as OrdersStatisticsMetric[] | undefined;

    const orderedMetrics = useMemo(() => {
        const base =
            responseMetrics && responseMetrics.length > 0 ? responseMetrics : selectedMetrics;
        const unique = Array.from(new Set(base)) as OrdersStatisticsMetric[];
        unique.sort((a, b) => (METRIC_ORDER[a] ?? 99) - (METRIC_ORDER[b] ?? 99));
        return unique;
    }, [responseMetrics, selectedMetrics]);

    const buckets = data?.series ?? [];

    const metricsOptions = useMemo(() => {
        const unique = new Set<OrdersStatisticsMetric>([
            ...ORDERS_STATISTICS_DEFAULT_METRICS,
            ...selectedMetrics,
        ]);
        if (responseMetrics) {
            responseMetrics.forEach((metric) => unique.add(metric));
        }
        return Array.from(unique).map((metric) => ({
            value: metric,
            label: getMetricLabel(metric),
        })) as Array<{ value: OrdersStatisticsMetric; label: string }>;
    }, [responseMetrics, selectedMetrics]);

    const categories = useMemo(() => {
        const granularityForLabels = data?.meta.granularity ?? granularity;
        return buckets.map((bucket) => {
            const pointDate = dayjs(bucket.bucket_start);
            if (granularityForLabels === "DAILY") {
                return pointDate.format("DD MMM");
            }
            if (granularityForLabels === "WEEKLY") {
                const endOfWeek = pointDate.add(6, "day");
                return `${pointDate.format("DD MMM")} - ${endOfWeek.format("DD MMM YYYY")}`;
            }
            if (granularityForLabels === "HALF_MONTHLY") {
                const half = pointDate.date() <= 15 ? "1-я половина" : "2-я половина";
                return `${half} ${pointDate.format("MMM YYYY")}`;
            }
            return pointDate.format("MMM YYYY");
        });
    }, [buckets, data?.meta.granularity, granularity]);

    const { chartOptions, hasData } = useMemo(() => {
        if (!data || orderedMetrics.length === 0 || buckets.length === 0) {
            return { chartOptions: undefined, hasData: false };
        }

        const yAxes: YAxisOptions[] = [];
        const axisIndex: Record<AxisId, number> = {} as Record<AxisId, number>;
        let hasSeriesData = false;

        const primaryAxisMetrics = orderedMetrics.filter((metric) => {
            const cfg = METRIC_CONFIG[metric];
            return !cfg || cfg.axis === "primary";
        });
        const secondaryAxisMetrics = orderedMetrics.filter(
            (metric) => METRIC_CONFIG[metric]?.axis === "secondary",
        );

        if (primaryAxisMetrics.length > 0) {
            const metricForAxis = primaryAxisMetrics[0]!;
            axisIndex.primary = yAxes.length;
            yAxes.push({
                title: { text: getMetricLabel(metricForAxis) },
                labels: {
                    formatter() {
                        return formatAxisLabel(this.value as number, metricForAxis);
                    },
                },
            });
        }

        if (secondaryAxisMetrics.length > 0) {
            const metricForAxis = secondaryAxisMetrics[0]!;
            axisIndex.secondary = yAxes.length;
            yAxes.push({
                title: { text: getMetricLabel(metricForAxis) },
                labels: {
                    formatter() {
                        return formatAxisLabel(this.value as number, metricForAxis);
                    },
                },
                opposite: yAxes.length > 0,
            });
        }

        if (Object.keys(axisIndex).length === 0) {
            axisIndex.primary = yAxes.length;
            yAxes.push({ title: { text: "Значение" } });
        }

        const series: SeriesOptionsType[] = orderedMetrics.map((metric) => {
            const config = METRIC_CONFIG[metric];
            const dataPoints = buildSeriesData(metric, buckets);
            if (!hasSeriesData && dataPoints.length > 0) {
                hasSeriesData = true;
            }
            const axisId: AxisId = config?.axis ?? "primary";
            const yAxisPosition = axisIndex[axisId] ?? 0;

            return {
                type: config?.seriesType ?? "line",
                name: getMetricLabel(metric),
                data: dataPoints,
                yAxis: yAxisPosition,
                tooltip: config?.tooltipLabel
                    ? { valueSuffix: ` ${config.tooltipLabel}` }
                    : undefined,
            } as SeriesOptionsType;
        });

        const options: Options = {
            chart: {
                height: DEFAULT_CHART_HEIGHT,
                scrollablePlotArea: { minWidth: 600 },
            },
            title: { text: "Статистика заказов" },
            xAxis: {
                categories,
            },
            yAxis: yAxes,
            legend: {
                enabled: series.length > 1,
            },
            tooltip: { shared: true, valueDecimals: 0 },
            plotOptions: {
                column: {
                    borderRadius: 4,
                    pointPadding: 0.1,
                },
                line: {
                    marker: { enabled: true },
                },
            },
            series,
        };

        return { chartOptions: options, hasData: hasSeriesData };
    }, [buckets, categories, data, orderedMetrics]);

    const totals = data?.totals;

    const merchantsSelectOptions = useMemo(
        () =>
            merchantOptions.map((merchant) => ({
                value: merchant.id,
                label: merchant.store_name,
            })),
        [merchantOptions],
    );

    const handleDateChange = (value: null | [Dayjs | null, Dayjs | null]) => {
        if (!value || !value[0] || !value[1]) return;
        setDateRange([value[0], value[1]]);
    };

    return (
        <Card title="Динамика заказов" variant="outlined" style={{ width: "100%" }}>
            <Flex vertical gap={24} style={{ width: "100%" }}>
                <Flex gap={16} wrap>
                    <RangePicker
                        value={dateRange}
                        onChange={handleDateChange}
                        allowClear={false}
                        format="YYYY-MM-DD"
                        style={{ minWidth: 280 }}
                    />

                    <Select
                        value={granularity}
                        options={GRANULARITY_OPTIONS}
                        onChange={(value) => setGranularity(value as StatisticsGranularity)}
                        style={{ minWidth: 200 }}
                        placeholder="Гранулярность"
                    />

                    <Select
                        mode="multiple"
                        value={selectedMetrics}
                        options={metricsOptions}
                        onChange={(value) => setSelectedMetrics(value as OrdersStatisticsMetric[])}
                        maxTagCount="responsive"
                        style={{ minWidth: 260 }}
                        placeholder="Метрики"
                        allowClear
                    />
                </Flex>

                <Flex gap={16} wrap>
                    <Select
                        value={deliveryType ?? undefined}
                        options={DELIVERY_TYPE_OPTIONS}
                        onChange={(value) =>
                            setDeliveryType((value ?? null) as DeliveryType | null)
                        }
                        allowClear
                        placeholder="Тип доставки"
                        style={{ minWidth: 200 }}
                        showSearch
                        optionFilterProp="label"
                    />

                    <Select
                        value={status ?? undefined}
                        options={ORDER_STATUS_OPTIONS}
                        onChange={(value) => setStatus((value ?? null) as OrdersStatus | null)}
                        allowClear
                        placeholder="Статус"
                        style={{ minWidth: 220 }}
                        showSearch
                        optionFilterProp="label"
                    />

                    <Select
                        value={preOrderFilter}
                        options={PRE_ORDER_SELECT_OPTIONS}
                        onChange={(value) => setPreOrderFilter(value as PreOrderFilterValue)}
                        style={{ minWidth: 200 }}
                    />

                    <Select
                        value={merchantId ?? undefined}
                        options={merchantsSelectOptions}
                        onChange={(value) => setMerchantId((value ?? null) as string | null)}
                        allowClear
                        placeholder="Магазин"
                        style={{ minWidth: 220 }}
                        showSearch
                        optionFilterProp="label"
                        loading={merchantsQuery.isFetching}
                    />
                </Flex>

                {!canRequestData && (
                    <Typography.Text type="secondary">
                        Выберите хотя бы одну метрику, чтобы построить график.
                    </Typography.Text>
                )}

                {canRequestData && isPending && <Skeleton active paragraph={{ rows: 8 }} />}

                {canRequestData && !isPending && isError && (
                    <Typography.Text type="danger">
                        {error?.response?.data?.detail ??
                            error?.message ??
                            "Не удалось загрузить статистику заказов"}
                    </Typography.Text>
                )}

                {canRequestData && !isPending && !isError && !hasData && (
                    <Typography.Text>Нет данных за выбранный период и фильтры</Typography.Text>
                )}

                {canRequestData && !isPending && !isError && hasData && chartOptions && (
                    <Flex vertical gap={16}>
                        {totals && orderedMetrics.length > 0 && (
                            <Flex gap={24} wrap="wrap">
                                {orderedMetrics.map((metric) => {
                                    const raw = totals[metric];
                                    const value = parseDecimal(raw);
                                    return (
                                        <div key={metric}>
                                            <Typography.Text type="secondary">
                                                {getMetricLabel(metric)}
                                            </Typography.Text>
                                            <Typography.Title level={4} style={{ margin: 0 }}>
                                                {formatAxisLabel(value, metric)}
                                            </Typography.Title>
                                        </div>
                                    );
                                })}
                            </Flex>
                        )}

                        <Chart options={chartOptions} />
                    </Flex>
                )}
            </Flex>
        </Card>
    );
}
